import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VideoCallRoundedIcon from '@mui/icons-material/VideoCallRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConferenceJitsi from './ConferenceJitsi';
import ConferenceLiveKit from './ConferenceLiveKit';
import { projectsAPI } from '../../services/api';

const ConferenceSelector = ({
  open,
  onClose,
  projectId,
  projectTitle,
  onConferenceCreated,
  existingLink,
  conferenceType,
  liveKitRoomName,
  autoJoin = false,
}) => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState(null);
  const [liveKitData, setLiveKitData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setSelectedType(null);
    setLiveKitData(null);
    setError(null);
    onClose();
  };

  const handleSelectLiveKit = async () => {
    setLoading(true);
    setError(null);

    try {
      const isJoiningExisting =
        conferenceType === 'livekit' && liveKitRoomName;

      const response = isJoiningExisting
        ? await projectsAPI.joinLiveKitConference(projectId)
        : await projectsAPI.createLiveKitConference(projectId);

      setLiveKitData({
        token: response.token,
        serverUrl: response.server_url,
      });

      if (onConferenceCreated && !isJoiningExisting) {
        onConferenceCreated({
          conference_type: 'livekit',
          livekit_room_name: response.room_name,
        });
      }

      setSelectedType('livekit');
    } catch (err) {
      setError(
        err.message || 'Не удалось подключиться к LiveKit-конференции'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      open &&
      autoJoin &&
      conferenceType === 'livekit' &&
      liveKitRoomName &&
      !liveKitData &&
      !loading
    ) {
      handleSelectLiveKit();
    }
  }, [open, autoJoin, conferenceType, liveKitRoomName]);

  if (selectedType === 'jitsi') {
    return (
      <ConferenceJitsi
        open={true}
        onClose={handleClose}
        projectId={projectId}
        projectTitle={projectTitle}
        onConferenceCreated={onConferenceCreated}
        existingLink={existingLink}
      />
    );
  }

  if (selectedType === 'livekit' && liveKitData) {
    return (
      <ConferenceLiveKit
        open={true}
        onClose={handleClose}
        token={liveKitData.token}
        serverUrl={liveKitData.serverUrl}
      />
    );
  }

  const corporateGreen = theme.palette.primary.main;
  const softGreen = alpha(corporateGreen, 0.09);
  const mutedText = theme.palette.text.secondary;

  const optionSx = {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: 48, sm: 54 },
    borderRadius: 999,
    pl: { xs: 2, sm: 2.5 },
    pr: { xs: 1, sm: 1.35 },
    py: 0.45,
    textTransform: 'none',
    justifyContent: 'flex-start',
    textAlign: 'left',
    boxShadow: 'none',
    transition: 'all 180ms ease',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.grey[900], 0.28),
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 440, md: 560 },
          maxWidth: 600,
          minHeight: { xs: 400, sm: 450 },
          borderRadius: { xs: 7, sm: 8.5 },
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
          background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.62)}, ${alpha(theme.palette.grey[100], 0.38)} 48%, ${alpha(corporateGreen, 0.10)}), radial-gradient(circle at 18% 12%, ${alpha(theme.palette.info.light, 0.22)}, transparent 38%), radial-gradient(circle at 88% 78%, ${alpha(corporateGreen, 0.20)}, transparent 42%)`,
          backgroundColor: alpha(theme.palette.common.white, 0.42),
          backdropFilter: 'blur(28px) saturate(145%)',
          WebkitBackdropFilter: 'blur(28px) saturate(145%)',
          boxShadow: `0 30px 100px ${alpha(theme.palette.common.black, 0.24)}`,
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label="Закрыть"
        sx={{
          position: 'absolute',
          top: { xs: 14, sm: 20 },
          right: { xs: 14, sm: 20 },
          zIndex: 2,
          width: { xs: 42, sm: 50 },
          height: { xs: 42, sm: 50 },
          borderRadius: '50%',
          color: alpha(theme.palette.text.primary, 0.65),
          backgroundColor: alpha(theme.palette.common.white, 0.56),
          border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.8),
          },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: { xs: 24, sm: 29 } }} />
      </IconButton>

      <DialogContent
        sx={{
          px: { xs: 1.3, sm: 2.5, md: 2.8 },
          py: { xs: 1.8, sm: 2.2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            borderRadius: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(145deg, ${alpha(
              corporateGreen,
              0.08
            )}, ${alpha(corporateGreen, 0.16)})`,
            color: corporateGreen,
            mb: { xs: 1.2, sm: 1.5 },
          }}
        >
          <VideoCallRoundedIcon sx={{ fontSize: { xs: 28, sm: 34 } }} />
        </Box>

        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.55rem' },
            lineHeight: 1.1,
            fontWeight: 800,
            letterSpacing: '-0.04em',
            color: theme.palette.text.primary,
            textAlign: 'center',
          }}
        >
          Конференция
        </Typography>

        <Typography
          sx={{
            mt: 0.7,
            mb: { xs: 1.4, sm: 1.8 },
            maxWidth: 620,
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            lineHeight: 1.45,
            color: mutedText,
            textAlign: 'center',
          }}
        >
          Выберите платформу для создания видеоконференции в проекте
        </Typography>

        {error && (
          <Box
            sx={{
              width: '100%',
              mb: 3,
              px: 2.5,
              py: 0.45,
              borderRadius: 5,
              color: 'error.main',
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
            }}
          >
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 1.2 },
            width: '100%',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PublicRoundedIcon sx={{ fontSize: '17px !important' }} />}
            onClick={() => setSelectedType('jitsi')}
            disabled={loading}
            sx={{
              ...optionSx,
              borderColor: alpha(theme.palette.text.primary, 0.1),
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.common.white, 0.48),
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': {
                borderColor: alpha(corporateGreen, 0.28),
                backgroundColor: alpha(theme.palette.common.white, 0.7),
                boxShadow: 'none',
              },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.82rem' }, fontWeight: 700 }}>
                Jitsi
              </Typography>
              <Typography sx={{ mt: 0.3, fontSize: { xs: '0.6rem', sm: '0.66rem' }, color: mutedText }}>
                Открытый сервис
              </Typography>
            </Box>
          </Button>

          <Button
            fullWidth
            variant="contained"
            startIcon={<VideoCallRoundedIcon sx={{ fontSize: '17px !important' }} />}
            onClick={handleSelectLiveKit}
            disabled={loading}
            sx={{
              ...optionSx,
              background: `linear-gradient(135deg, ${corporateGreen}, ${alpha(
                corporateGreen,
                0.72
              )})`,
              color: theme.palette.primary.contrastText,
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${corporateGreen})`,
                boxShadow: 'none',
              },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.82rem' }, fontWeight: 700 }}>
                LiveKit
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: { xs: '0.6rem', sm: '0.66rem' },
                  color: alpha(theme.palette.primary.contrastText, 0.78),
                }}
              >
                Рекомендуем
              </Typography>
            </Box>
          </Button>
        </Box>

        <Box
          sx={{
            width: '100%',
            mt: { xs: 1.2, sm: 1.6 },
            px: { xs: 1, sm: 1.25 },
            py: { xs: 0.85, sm: 1 },
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.2,
            borderRadius: 5,
            backgroundColor: softGreen,
            border: `1px solid ${alpha(corporateGreen, 0.06)}`,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              mt: 0.2,
              fontSize: { xs: 18, sm: 20 },
              color: corporateGreen,
              flexShrink: 0,
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '0.64rem', sm: '0.7rem' },
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              LiveKit — наш основной сервис для видеоконференций.
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: { xs: '0.6rem', sm: '0.66rem' },
                lineHeight: 1.5,
                color: mutedText,
              }}
            >
              Стабильная работа, высокое качество и дополнительные возможности.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              minWidth: { xs: 76, sm: 88 },
              minHeight: 32,
              px: 2,
              borderRadius: 999,
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: 600,
              color: alpha(theme.palette.text.primary, 0.72),
              backgroundColor: alpha(theme.palette.common.white, 0.48),
              border: `1px solid ${alpha(theme.palette.common.white, 0.85)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.75),
              },
            }}
          >
            Отмена
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConferenceSelector;
